import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import useDocumentTitle from "@/hooks/useDocumentTitle";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Plus, House, MagnifyingGlass, Printer, CurrencyEur } from "@phosphor-icons/react";
import InvoicedToggle from "@/components/InvoicedToggle";

const eur = (n) => (n ?? 0).toLocaleString("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 2 });

export default function HomeInterventionsPage() {
  useDocumentTitle("Interventi a domicilio");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    const params = { intervention_type: "home" };
    if (q) params.q = q;
    const { data } = await api.get("/interventions", { params });
    setItems(data);
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q]);

  const totalRevenue = items.reduce((s, it) => s + (it.price_final || 0), 0);

  return (
    <div className="p-6 lg:p-10 max-w-[1200px] mx-auto">
      <header className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-md bg-primary/10 text-primary grid place-items-center">
            <House size={22} weight="duotone" />
          </div>
          <div>
            <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground">// DOMICILIO</div>
            <h1 className="text-4xl font-display font-bold tracking-tight">Interventi a domicilio</h1>
            <p className="text-muted-foreground text-sm mt-1">
              {items.length} interventi · Fatturato totale {eur(totalRevenue)}
            </p>
          </div>
        </div>
        <Link to="/domicilio/nuovo">
          <Button data-testid="new-home-btn" className="rounded-full gap-2">
            <Plus size={16} weight="bold" /> Nuovo intervento a domicilio
          </Button>
        </Link>
      </header>

      <Card className="p-4 border-border/70 rounded-lg mb-4">
        <div className="relative">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Cerca per cliente, codice, descrizione…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="home-search"
            className="pl-9"
          />
        </div>
      </Card>

      <Card className="border-border/70 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary/60">
              <tr className="text-left">
                <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Codice</th>
                <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Cliente</th>
                <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Descrizione</th>
                <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Data</th>
                <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-muted-foreground text-right">Importo</th>
                <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Fattura</th>
                <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-muted-foreground text-right">Azione</th>
              </tr>
            </thead>
            <tbody data-testid="home-list-body">
              {loading && <tr><td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">Caricamento…</td></tr>}
              {!loading && items.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-12 text-center text-muted-foreground">
                  Nessun intervento a domicilio. <Link to="/domicilio/nuovo" className="text-primary underline underline-offset-4">Creane uno</Link>.
                </td></tr>
              )}
              {items.map((it) => (
                <tr key={it.id} className="border-t border-border/50 hover:bg-secondary/40"
                    style={{ transition: "background-color 0.15s ease" }}>
                  <td className="px-4 py-3">
                    <Link to={`/interventi/${it.id}`} className="font-mono text-xs text-primary hover:underline">
                      {it.code}
                    </Link>
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium">{it.customer_surname} {it.customer_name}</div>
                    <div className="font-mono text-xs text-muted-foreground">{it.customer_phone}</div>
                  </td>
                  <td className="px-4 py-3 max-w-[320px]"><div className="truncate">{it.work_description}</div></td>
                  <td className="px-4 py-3 font-mono text-xs text-muted-foreground">
                    <div>{new Date(it.created_at).toLocaleDateString("it-IT")}</div>
                    <div className="text-[10px]">{new Date(it.created_at).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}</div>
                  </td>
                  <td className="px-4 py-3 text-right font-mono">
                    {it.price_final != null ? eur(it.price_final) : <span className="text-muted-foreground">—</span>}
                  </td>
                  <td className="px-4 py-3">
                    <InvoicedToggle
                      intervention={it}
                      onChange={(u) => setItems((prev) => prev.map((x) => x.id === u.id ? u : x))}
                    />
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Link to={`/interventi/${it.id}/scontrino`}>
                      <Button size="sm" variant="outline" data-testid={`home-print-${it.code}`} className="gap-1">
                        <Printer size={14} /> Ristampa
                      </Button>
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
